"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.clearElmInfoCache = exports.retrieveELMInfo = void 0;
const cql_execution_1 = require("cql-execution");
const MeasureBundleHelpers_1 = require("../MeasureBundleHelpers");
const DetailedResultsBuilder_1 = require("../../calculation/DetailedResultsBuilder");
const ValueSetHelper_1 = require("../../execution/ValueSetHelper");
const Enums_1 = require("../../types/Enums");
const DetailedResultsHelpers_1 = require("../DetailedResultsHelpers");
// Cache entry should expire if it hasn't been accessed in 10 minutes
const CACHE_EXPIRE_TIME = 600000;
const ELMInfoCache = new Map();
/**
 * Calculates cqls, elmJSONs, rootLibIdentifier, codeService, rep, and vsMap info or retrieves from cache if available and enabled
 * @param measure {Object} the FHIR Measure that Calculation is being run on
 * @param measureBundle {Object} the FHIR Bundle containing the Measure and supplemental resources
 * @param valueSets {Array} the FHIR Valueset resources referenced by the Libraries within the Measure
 * @param useElmJsonsCaching {boolean} a flag that, when set to true, enables caching of cqls, elmJSONs,
 * rootLibIdentifier, codeService, rep, and vsMap data for quicker use in subsequent runs
 * @returns {Object} an object containing cqls, elmJSONs, rootLibIdentifier, codeService, rep, and vsMap
 */
function retrieveELMInfo(measure, measureBundle, valueSets, useElmJsonsCaching) {
    var _a, _b;
    const { id, version } = measure;
    const key = `${id}-${version}`;
    if (!(ELMInfoCache.get(key) && cacheEntryIsValid((_a = ELMInfoCache.get(key)) === null || _a === void 0 ? void 0 : _a.lastAccessed))) {
        const vsMap = (0, ValueSetHelper_1.valueSetsForCodeService)(valueSets);
        const { cqls, rootLibIdentifier, elmJSONs } = (0, MeasureBundleHelpers_1.extractLibrariesFromMeasureBundle)(measureBundle, measure);
        // add expressions for collecting for all measure observations
        (_b = measure.group) === null || _b === void 0 ? void 0 : _b.forEach(group => {
            var _a, _b;
            (_b = (_a = group.population) === null || _a === void 0 ? void 0 : _a.filter(population => (0, MeasureBundleHelpers_1.codeableConceptToPopulationType)(population.code) === Enums_1.PopulationType.OBSERV)) === null || _b === void 0 ? void 0 : _b.forEach(obsrvPop => {
                var _a, _b;
                const msrPop = (0, DetailedResultsHelpers_1.findObsMsrPopl)(group, obsrvPop);
                if (((_a = msrPop === null || msrPop === void 0 ? void 0 : msrPop.criteria) === null || _a === void 0 ? void 0 : _a.expression) && ((_b = obsrvPop.criteria) === null || _b === void 0 ? void 0 : _b.expression)) {
                    const mainLib = elmJSONs.find(elm => elm.library.identifier.id === rootLibIdentifier.id);
                    if (mainLib) {
                        const elmFunction = (0, MeasureBundleHelpers_1.isEpisodeOfCareGroup)(measure, group)
                            ? (0, DetailedResultsBuilder_1.generateEpisodeELMJSONFunction)(obsrvPop.criteria.expression, msrPop.criteria.expression)
                            : (0, DetailedResultsBuilder_1.generateBooleanELMJSONFunction)(obsrvPop.criteria.expression);
                        mainLib.library.statements.def.push(elmFunction);
                    }
                }
            });
        });
        const codeService = new cql_execution_1.CodeService(vsMap);
        const rep = new cql_execution_1.Repository(elmJSONs);
        if (useElmJsonsCaching) {
            ELMInfoCache.set(key, { cqls, elmJSONs, rootLibIdentifier, codeService, rep, vsMap, lastAccessed: new Date() });
        }
        else {
            return { cqls, elmJSONs, rootLibIdentifier, codeService, rep, vsMap };
        }
    }
    else {
        ELMInfoCache.set(key, Object.assign(Object.assign({}, ELMInfoCache.get(key)), { lastAccessed: new Date() }));
    }
    // We need typecasting here (and above) since theoretically Map.get() can return undefined, even though we know the key is present at this point
    return ELMInfoCache.get(key);
}
exports.retrieveELMInfo = retrieveELMInfo;
function clearElmInfoCache() {
    ELMInfoCache.clear();
}
exports.clearElmInfoCache = clearElmInfoCache;
/**
 *
 * @param lastAccessed {Object} a JS date dignifying the last accessed moment of a cache entry
 * @returns {boolean} true if the last accessed date is less than 10 minutes before the current date
 */
function cacheEntryIsValid(lastAccessed) {
    if (!lastAccessed) {
        return false;
    }
    const now = new Date();
    return now.getTime() - lastAccessed.getTime() <= CACHE_EXPIRE_TIME;
}
//# sourceMappingURL=ELMInfoCache.js.map